import { CustomEpic, PayloadAction } from 'types/types';
import { ofType } from 'redux-observable';
import toast from 'react-hot-toast';
import { of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import * as Sentry from '@sentry/react';
import { handleAjaxSuccess } from 'utils/epics/handleAjaxSuccess';
import { handleAjaxError } from 'utils/epics/handleAjaxError';
import {
  UploadAppFileActionPayload,
  uploadAppFileFailure,
  uploadAppFileSuccess,
} from '../actions';
import { UPLOAD_APP_FILE_START } from '../actionTypes';
import { getUploadAppFileEndpoint } from '../constants';
import { getAuthHeaders } from 'api/getAuthHeaders';
import { fileToBase64 } from 'utils/file/fileToBase64';
import { convertSnakeToCamelCase } from 'utils/convertSnakeToCamelCase';

// TODO: Re-enable deferred file upload for new file API.
// Old implementation: https://gitlab.com/PeteHealth/PH-Apps/-/blob/2dc011660ae344af41a60cffc725c7593ebd22a2/therapist/src/modules/dropbox/epics/uploadDropboxFile$.ts
export const uploadAppFile$: CustomEpic<UploadAppFileActionPayload> = (
  action$,
  state$,
  { postApi$ },
) =>
  action$.pipe(
    ofType(UPLOAD_APP_FILE_START),
    // `mergeMap` at both levels, not `switchMap`. Two uploads are two different
    //   files: neither supersedes the other, and dropping one loses a document.
    //
    // The reason it mattered more than a discarded response: `post$` is
    //   `from(axiosPromise)`, and a promise cannot be cancelled. Unsubscribing
    //   never stopped the upload, it only stopped listening — so the bytes
    //   reached the server, `uploadAppFileSuccess` never fired for them, the
    //   list stayed empty, and a therapist who could not see the document
    //   uploaded it again. The case ends up holding it twice.
    //
    // It also stranded the `toast.loading`, whose duration is `Infinity`:
    //   `toast.dismiss` lives in the success and error handlers, and the
    //   cancelled inner reached neither. The spinner stayed for the rest of the
    //   session.
    //
    // Uploading two documents back to back — both sides of an insurance card —
    //   is the ordinary case rather than a race, and base64 inside a JSON body
    //   keeps each request in flight long enough to overlap.
    // The read is caught here rather than allowed to reject.
    //
    // `fileToBase64` rejects on `reader.onerror`, and an `async` mapper turns
    //   that into an error on the epic's output. `combineEpics` merges every
    //   epic into one stream, so that error used to end *all* of them: no
    //   request, booking, payment or sync would run again until the page was
    //   reloaded, with the UI still rendering as though nothing were wrong.
    //   `rootEpic` now catches and resubscribes, so this is no longer fatal —
    //   but recovering silently is not enough, because the therapist picked a
    //   file and would see nothing happen at all.
    //
    // Resolved as a discriminated result instead of throwing, so the failure
    //   travels as a value and the next stage decides. Rejecting and catching
    //   downstream is not equivalent: `catchError` on the outer stream would
    //   unsubscribe the source and take the concurrent uploads with it, which
    //   is the behaviour the `mergeMap` above exists to prevent.
    mergeMap(async (action: PayloadAction<UploadAppFileActionPayload>) => {
      try {
        return {
          didRead: true as const,
          action: {
            payload: {
              ...action.payload,
              file: (await fileToBase64(action.payload.file)) as string,
            },
            type: action.type,
          },
        };
      } catch (error) {
        return { didRead: false as const, error, filename: action.payload.filename };
      }
    }),
    mergeMap(result => {
      if (!result.didRead) {
        // No `toast.dismiss` needed: the loading toast is created below, after
        //   the read, so a failed read never opened one to strand.
        toast.error('That file could not be read. Try choosing it again.');
        Sentry.captureException(result.error, {
          tags: { area: 'file-upload-read' },
        });
        return of(uploadAppFileFailure({ message: 'Could not read the file' }));
      }

      const { action } = result;
      const { caseId, filename, leadId, file, upload_type } = action.payload;
      const toastId = toast.loading('Uploading file');
      return postApi$({
        endpoint: getUploadAppFileEndpoint({ caseId }),
        headers: getAuthHeaders(state$.value),
        payload: {
          file_name: filename,
          file_contents: file,
          upload_type,
        },
      }).pipe(
        handleAjaxSuccess(uploadAppFileSuccess, result => {
          result.filename = filename;
          result.caseId = caseId;
          result.uploadType = upload_type;
          result.leadId = leadId;
          toast.dismiss(toastId);
          toast.success('File uploaded');
          return convertSnakeToCamelCase(result);
        }),
        handleAjaxError(
          uploadAppFileFailure,
          'There was an issue uploading your file',
          toastId,
        ),
      );
    }),
  );
